import { useRef, useState } from "react";
import {
  DEFAULT_SETTING_REFLECTION,
  WorkflowType,
  SettingsResponsePC,
  SettingsResponseReflection,
} from "@pages/data-management/data-management-types";
import { ReflectionFilterPopover } from "@pages/data-management/reflection-filter-popover";
import ArrowDownIcon from "@assets/icons/new/arrow-down_10px.svg?react";
import ArrowUpIcon from "@assets/icons/new/arrow-up_10px.svg?react";
import InfoSvg from "@assets/icons/new/info-circle_24px.svg?react";
import { FaroBetaBadge } from "@components/common/faro-beta-badge";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { Alert, FaroSwitch } from "@faro-lotv/flat-ui";
import { Box, CircularProgress, Collapse, Stack, SvgIcon } from "@mui/material";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";

interface Props {
  /** Type of the workflow, automatic or manual. */
  workflowType: WorkflowType,
  /** Settings object received by getPublishSettings. */
  settingPC: SettingsResponsePC,
  /** Callback to set new value of settingPC. */
  setSettingPC: (value: React.SetStateAction<SettingsResponsePC>) => void,
  /** Settings object received by getPublishSettings. */
  settingReflection: SettingsResponseReflection,
  /** Callback to set new value of settingReflection. */
  setSettingReflection: (value: React.SetStateAction<SettingsResponseReflection>) => void,
}

/** Component with UI elements for publication settings. */
export function PublishOptions({
  workflowType,
  settingPC,
  setSettingPC,
  settingReflection,
  setSettingReflection,
}: Props): JSX.Element {
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const isLoading = !settingPC.modifiedAt || !settingReflection.modifiedAt;
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Reference for the reflection filter popover.
  const reflPopoverRef = useRef<HTMLDivElement | HTMLAnchorElement | null>(null);
  // Flag if the reflection filter popover is visible.
  const [isShowReflPopover, setIsShowReflPopover] = useState<boolean>(false);

  function setShouldGeneratePC(isEnabled: boolean): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    setSettingPC((prev) => ({ ...prev, value: isEnabled }));
  }
  function setShouldApplyReflection(isEnabled: boolean): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    setSettingReflection((prev) => ({ ...prev, value: isEnabled }));
  }

  return (
    <Stack gap="15px">
      <Stack
        direction="row"
        sx={{ cursor: "pointer" }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <FaroIconButton
          buttonSize="24px"
          iconSize="16px"
          component={isCollapsed ? ArrowUpIcon : ArrowDownIcon}
        />

        <Box sx={{
          ml: "5px",
          mr: "7px",
          mt: "1px",
          fontSize: "14px",
          fontWeight: "700",
          userSelect: "none",
        }}>
          Publishing options
        </Box>

        <CircularProgress size={20} sx={{
          mt: "2px",
          visibility: isLoading ? "visible" : "hidden",
        }}/>
      </Stack>

      <Collapse in={!isCollapsed} sx={{ ml: "27px" }}>
        { workflowType === "auto" ? (
          <Stack gap="10px">
            <FaroSwitch
              label="Generate point cloud"
              fullWidth={true}
              checked={!isDevModeEnabled || settingPC.value}
              disabled={!isDevModeEnabled || isLoading}
              onToggle={() => {
                setShouldGeneratePC(!settingPC.value);
                // If PC is being enabled, and DEFAULT_SETTING_REFLECTION is true (later probably),
                // then also enable the reflection filter.
                setShouldApplyReflection(!settingPC.value && DEFAULT_SETTING_REFLECTION);
              }}
            />

            <FaroSwitch
              label={
                <Stack
                  direction="row"
                  gap="5px"
                  sx={{ alignItems: "baseline" }}
                >
                  <span>Reflection filter</span>

                  <FaroBetaBadge sx={{ bottom: "2px" }} />

                  <Box
                    ref={reflPopoverRef}
                    onMouseEnter={() => setIsShowReflPopover(true)}
                    onMouseLeave={() => setIsShowReflPopover(false)}
                  >
                    <SvgIcon
                      inheritViewBox
                      component={InfoSvg}
                      sx={{ mb: "-6px" }}
                    />
                  </Box>

                  <ReflectionFilterPopover
                    popoverRef={reflPopoverRef}
                    isShowPopover={isShowReflPopover}
                    setIsShowPopover={setIsShowReflPopover}
                  />
                </Stack>
              }
              fullWidth={true}
              checked={settingReflection.value}
              disabled={isLoading || !settingPC.value}
              onToggle={() => {
                setShouldGeneratePC(true);
                setShouldApplyReflection(!settingReflection.value);
              }}
            />
          </Stack>
        ) : (
          <Alert
            title="Publishing options will be set up later during the publish step."
            variant="info"
          />
        )}
      </Collapse>
    </Stack>
  );
}
